import React, { FC } from "react";

import { Button } from "@UI/Components/Button/Button";
import { Divider } from "@UI/Components/Divider/Divider";
import { Flex } from "@UI/Components/Flex/Flex";
import { Icon } from "@UI/Components/Icon/Icon";
import { Apple } from "@UI/Components/Icons/Apple";
import { Facebook } from "@UI/Components/Icons/Facebook";
import { Google } from "@UI/Components/Icons/Google";
import { VStack } from "@UI/Components/Stack/Stack";
import { Text } from "@UI/Components/Text/Text";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import {
  TSsoProvider,
  isSsoProviderConfigured,
} from "@modules/Authentication/services/ssoProviders";

import messages from "../../messages";

const BUTTON_STYLES = {
  height: "48px",
  borderRadius: "10px",
  fontSize: "md",
  fontWeight: "500",
  width: "100%",
} as const;

const PROVIDER_STYLES = {
  google: {
    // The shorthand, not `borderWidth`: the solid variant sets
    // `borderBottom: none`, which leaves that one side styleless and unpainted.
    border: "1px solid",
    background: "white",
    color: "gray.800",
    borderColor: "gray.400",
    _hover: { background: "gray.100" },
  },
  facebook: {
    background: "#1877F2",
    color: "white",
    _hover: { background: "#0C63D4" },
  },
  apple: {
    background: "black",
    color: "white",
    _hover: { background: "gray.800" },
  },
} as const;

export interface ISsoButtonsProps {
  onSignIn: (provider: TSsoProvider) => void;
  /** The provider whose popup is open, or whose session is being created. */
  pendingProvider?: TSsoProvider;
  isDisabled?: boolean;
}

/**
 * "Continue with…", beneath the email form it is an alternative to.
 *
 * A provider with no client id configured is left out rather than shown broken —
 * see `services/ssoProviders.ts`.
 */
export const SsoButtons: FC<ISsoButtonsProps> = ({
  onSignIn,
  pendingProvider,
  isDisabled,
}) => {
  const providers = (["google", "facebook", "apple"] as const).filter(
    isSsoProviderConfigured
  );

  if (!providers.length) return null;

  return (
    <VStack alignItems="stretch" spacing="12px">
      <Flex alignItems="center" gap="16px">
        <Divider borderColor="gray.300" />
        <Text fontSize="sm" color="gray.600" flexShrink={0}>
          <FormattedMessage {...messages["taskCreation.auth.sso.or"]} />
        </Text>
        <Divider borderColor="gray.300" />
      </Flex>
      {providers.includes("google") && (
        <Button
          type="button"
          onClick={() => onSignIn("google")}
          isLoading={pendingProvider === "google"}
          isDisabled={isDisabled}
          leftIcon={<Icon as={Google} width="18px" height="18px" />}
          {...BUTTON_STYLES}
          {...PROVIDER_STYLES.google}
        >
          <FormattedMessage {...messages["taskCreation.auth.sso.google"]} />
        </Button>
      )}
      {providers.includes("facebook") && (
        <Button
          type="button"
          onClick={() => onSignIn("facebook")}
          isLoading={pendingProvider === "facebook"}
          isDisabled={isDisabled}
          leftIcon={<Icon as={Facebook} width="18px" height="18px" />}
          {...BUTTON_STYLES}
          {...PROVIDER_STYLES.facebook}
        >
          <FormattedMessage {...messages["taskCreation.auth.sso.facebook"]} />
        </Button>
      )}
      {providers.includes("apple") && (
        <Button
          type="button"
          onClick={() => onSignIn("apple")}
          isLoading={pendingProvider === "apple"}
          isDisabled={isDisabled}
          leftIcon={<Icon as={Apple} width="18px" height="18px" />}
          {...BUTTON_STYLES}
          {...PROVIDER_STYLES.apple}
        >
          <FormattedMessage {...messages["taskCreation.auth.sso.apple"]} />
        </Button>
      )}
    </VStack>
  );
};
